import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ScrollReveal from "./ScrollReveal";


const useCountUp = (target: number, duration = 1800, started: boolean) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!started) { setValue(0); return; }
    const steps = 50;
    const stepTime = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const eased = 1 - Math.pow(1 - step / steps, 3);
      setValue(Math.round(target * eased));
      if (step >= steps) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [started, target, duration]);
  return value;
};

const AnimatedNumber = ({ value, suffix = "" }: { value: string; suffix?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) {
        setStarted(true);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  const match = value.match(/^([+-]?)(\d+)/);
  const sign = match?.[1] || "";
  const target = match ? parseInt(match[2], 10) : 0;
  const rest = match ? value.slice(match[0].length) : value;
  const animated = useCountUp(target, 1800, started);

  return (
    <div ref={ref} className={`font-mono text-4xl lg:text-5xl font-bold transition-all duration-700 ${started ? "opacity-100 scale-100" : "opacity-0 scale-75"}`}>
      <span className="text-gold">{started ? `${sign}${animated}${rest}` : `${sign}0${rest}`}</span>
      {suffix && <span className="text-gold text-2xl">{suffix}</span>}
    </div>
  );
};

const AnimatedStat = ({ text, duration = 1800 }: { text: string; duration?: number }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) {
        setStarted(true);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  const match = text.match(/^([\d\s]+)/);
  if (!match) {
    return <p ref={ref} className="font-mono text-gold font-bold text-lg">{text}</p>;
  }

  const numStr = match[1].trim().replace(/\s/g, "");
  const target = parseInt(numStr, 10);
  const rest = text.slice(match[0].length);
  const hasSpace = match[1].includes(" ") || rest.length > 0;
  const animated = useCountUp(target, duration, started);

  const formatted = match[1].trim().includes(" ")
    ? animated.toLocaleString("fr-FR")
    : String(animated);

  const spacer = rest.length > 0 ? " " : "";

  return (
    <p ref={ref} className="font-mono text-gold font-bold text-lg">
      {started ? `${formatted}${spacer}${rest}` : `0${spacer}${rest}`}
    </p>
  );
};

const MetricsSection = () => {
  const { t } = useTranslation();

  const metrics = [
    { value: "-40%", label: t("metrics.stat1") },
    { value: "-30%", label: t("metrics.stat2") },
    { value: "-25%", label: t("metrics.stat3") },
    { value: "12", suffix: t("metrics.stat4suffix"), label: t("metrics.stat4label") },
  ];

  return (
    <section id="metrics" className="py-8 lg:py-14 bg-card relative" aria-labelledby="metrics-heading">
      <div className="container mx-auto px-4 space-y-6 lg:space-y-10">
        <ScrollReveal>
          <h2 id="metrics-heading" className="font-display text-3xl lg:text-5xl text-foreground text-center">
            {t("metrics.title")}
          </h2>
        </ScrollReveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {metrics.map((m, i) => (
            <ScrollReveal key={i} delay={i * 0.1}>
              <div className="text-center space-y-2">
                <AnimatedNumber value={m.value} suffix={m.suffix} />
                <p className="text-sm text-muted-foreground font-body">{m.label}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal>
          <div className="card-surface p-8 border-glow glow-cyan-strong max-w-3xl mx-auto space-y-4">
            <span className="font-mono text-xs text-gold uppercase tracking-wider">{t("metrics.rexTitle")}</span>
            <p className="text-foreground font-body leading-relaxed">{t("metrics.rexDesc")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-background/50 rounded-lg p-3 border border-border/50">
                <AnimatedStat text={t("metrics.rex1")} />
                <p className="text-xs text-muted-foreground font-body">{t("metrics.rex1desc")}</p>
              </div>
              <div className="bg-background/50 rounded-lg p-3 border border-border/50">
                <AnimatedStat text={t("metrics.rex2")} />
                <p className="text-xs text-muted-foreground font-body">{t("metrics.rex2desc")}</p>
              </div>
              <div className="bg-background/50 rounded-lg p-3 border border-border/50">
                <AnimatedStat text={t("metrics.rex3")} />
                <p className="text-xs text-muted-foreground font-body">{t("metrics.rex3desc")}</p>
              </div>
              <div className="bg-background/50 rounded-lg p-3 border border-border/50">
                <AnimatedStat text={t("metrics.rex4")} duration={2200} />
                <p className="text-xs text-muted-foreground font-body">{t("metrics.rex4desc")}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground font-body border-t border-border/30 pt-3">
              <span className="text-foreground font-medium">{t("metrics.rexConclusion")}</span> {t("metrics.rexConclusionBody")}
            </p>
            <Link
              to="/blog/aquaphys-transforme-les-reseaux-d-eau-du-batiment-en-levier-de-performance"
              className="inline-flex items-center gap-1.5 text-sm text-primary font-body hover:underline"
            >
              Lire l'étude de cas complète <ArrowRight size={14} />
            </Link>

          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default MetricsSection;
